import { useEffect, useMemo, useRef, useState } from 'react'
import TeamHeader from '../TeamHeader/TeamHeader'

import './TeamDirectory.css'
import { apiRequest, errorMessage } from './supportApi'

// Data comes from two existing endpoints:
//   /portal/api/support/departments/   teams that have a leader record
//   /api/inventory/fnn-users/          FNN people and the team on their profile

interface Department {
    id: number
    name: string
    leader: number | null          // ClientUserProfile id (same id as FnnPerson.id)
    leader_name: string | null
}

interface FnnPerson {
    id: number
    user_id: number
    username: string
    email: string
    first_name: string
    last_name: string
    department: string | null
}

interface TeamRow {
    name: string
    leader: FnnPerson | null
    leaderName: string | null       // from the departments endpoint, in case the person isn't in the user list
    members: FnnPerson[]            // people whose profile is in this team, leader excluded
    hasRecord: boolean              // exists in the departments endpoint
}

const NO_TEAM = 'No team on profile'

type View = 'table' | 'chart'
const VIEW_KEY = 'fnn-team-directory-view'

function savedView(): View {
    try { return localStorage.getItem(VIEW_KEY) === 'chart' ? 'chart' : 'table' } catch { return 'table' }
}

const fullName = (p: FnnPerson) => `${p.first_name} ${p.last_name}`.trim() || p.username

function initials(name: string) {
    const parts = name.trim().split(/\s+/)
    return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?'
}

function Person({ person, name }: { person: FnnPerson | null; name: string }) {
    const content = (
        <>
            <span className="td-avatar" aria-hidden="true">{initials(name)}</span>
            <span className="td-person-text">
                <span className="td-person-name">{name}</span>
                {person?.email && <span className="td-person-email">{person.email}</span>}
            </span>
        </>
    )
    return person?.email
        ? <a className="td-person" href={`mailto:${person.email}`} title={`Email ${name} (${person.email})`}>{content}</a>
        : <span className="td-person" title={name}>{content}</span>
}

// ─── Chart view ───────────────────────────────────────────────────────────────

function ChartNode({ person, name, variant, note }: {
    person: FnnPerson | null; name: string; variant: 'leader' | 'member'; note?: string
}) {
    const body = (
        <>
            <span className="td-avatar" aria-hidden="true">{initials(name)}</span>
            <span className="td-person-text">
                <span className="td-person-name">{name}</span>
                {note && <span className="tdc-node-note">{note}</span>}
            </span>
        </>
    )
    return person?.email
        ? <a className={`tdc-node is-${variant}`} href={`mailto:${person.email}`} title={`Email ${name} (${person.email})`}>{body}</a>
        : <span className={`tdc-node is-${variant}`}>{body}</span>
}

function ChartView({ teams, totalPeople }: { teams: TeamRow[]; totalPeople: number }) {
    const scrollRef = useRef<HTMLDivElement>(null)

    // On narrow screens the chart is wider than the page: open it centred on FNN
    useEffect(() => {
        const el = scrollRef.current
        if (el) el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2
    }, [teams.length])

    return (
        <div ref={scrollRef} className="tdc-scroll" role="region" aria-label="Team chart" tabIndex={0}>
            <div className="tdc-tree">
                <div className="tdc-root">
                    <strong>FNN</strong>
                    <span>{totalPeople} people</span>
                </div>

                <ul className="tdc-teams">
                    {teams.map(t => {
                        const leaderElsewhere = t.leader && t.leader.department && t.leader.department !== t.name
                        const unassigned = t.name === NO_TEAM
                        return (
                            <li key={t.name} className={unassigned ? 'is-unassigned' : ''}>
                                <div className="tdc-team">
                                    <strong>{t.name}</strong>
                                    <span>{t.members.length + (t.leaderName ? 1 : 0)} {t.members.length + (t.leaderName ? 1 : 0) === 1 ? 'person' : 'people'}</span>
                                </div>

                                {!unassigned && (
                                    <div className="tdc-stem">
                                        {t.leaderName
                                            ? <ChartNode person={t.leader} name={t.leaderName} variant="leader"
                                                note={leaderElsewhere ? `Profile in ${t.leader!.department}` : 'Leader'} />
                                            : <span className="tdc-node is-missing">No leader</span>}
                                    </div>
                                )}

                                {t.members.length > 0 && (
                                    <ul className="tdc-members">
                                        {t.members.map(p => (
                                            <li key={p.id}><ChartNode person={p} name={fullName(p)} variant="member" /></li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        )
                    })}
                </ul>
            </div>
        </div>
    )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function TeamDirectory() {
    const [departments, setDepartments] = useState<Department[] | null>(null)
    const [people, setPeople] = useState<FnnPerson[] | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [query, setQuery] = useState('')
    const [view, setView] = useState<View>(savedView)

    const changeView = (v: View) => {
        setView(v)
        try { localStorage.setItem(VIEW_KEY, v) } catch { /* private mode: just don't remember */ }
    }

    const load = async () => {
        setError(null)
        const [d, u] = await Promise.all([
            apiRequest<Department[]>('/portal/api/support/departments/'),
            apiRequest<FnnPerson[]>('/api/inventory/fnn-users/'),
        ])
        if (!d.ok || !u.ok) {
            setError(errorMessage(!d.ok ? d.data : u.data, 'The team directory couldn’t be loaded.'))
            return
        }
        setDepartments(d.data)
        setPeople(u.data)
    }

    useEffect(() => { load() }, [])

    const teams = useMemo<TeamRow[]>(() => {
        if (!departments || !people) return []
        const byId = new Map(people.map(p => [p.id, p]))
        const names = new Set<string>([
            ...departments.map(d => d.name),
            ...people.map(p => p.department).filter((n): n is string => !!n),
        ])

        const rows: TeamRow[] = Array.from(names).map(name => {
            const dept = departments.find(d => d.name === name)
            const leader = dept?.leader != null ? byId.get(dept.leader) ?? null : null
            return {
                name,
                leader,
                leaderName: leader ? fullName(leader) : dept?.leader_name ?? null,
                members: people
                    .filter(p => p.department === name && p.id !== dept?.leader)
                    .sort((a, b) => fullName(a).localeCompare(fullName(b))),
                hasRecord: !!dept,
            }
        }).sort((a, b) => a.name.localeCompare(b.name))

        const unassigned = people.filter(p => !p.department)
        if (unassigned.length) {
            rows.push({ name: NO_TEAM, leader: null, leaderName: null, members: unassigned, hasRecord: true })
        }
        return rows
    }, [departments, people])

    const visible = useMemo(() => {
        const q = query.toLowerCase().trim()
        if (!q) return teams
        return teams
            .map(t => {
                const teamMatch = t.name.toLowerCase().includes(q)
                const leaderMatch = (t.leaderName ?? '').toLowerCase().includes(q) ||
                    (t.leader?.email ?? '').toLowerCase().includes(q)
                const members = teamMatch ? t.members : t.members.filter(p =>
                    fullName(p).toLowerCase().includes(q) || p.email.toLowerCase().includes(q))
                return teamMatch || leaderMatch || members.length ? { ...t, members } : null
            })
            .filter((t): t is TeamRow => !!t)
    }, [teams, query])

    const realTeams = teams.filter(t => t.name !== NO_TEAM)
    const leaderless = realTeams.filter(t => !t.leaderName)

    return (
        <div>
            <TeamHeader title="Teams" />
            <div className="customer-directory-body td-root">
                <header className="td-top">
                    <div>
                        <h1>Teams</h1>
                        {people && (
                            <p>
                                {realTeams.length} teams and {people.length} people.{' '}
                                {leaderless.length === 0
                                    ? 'Every team has a leader.'
                                    : `${leaderless.map(t => t.name).join(', ')} ${leaderless.length === 1 ? 'has' : 'have'} no leader yet.`}
                            </p>
                        )}
                    </div>
                    <div className="td-controls">
                        <div className="td-toggle" role="group" aria-label="View">
                            <button aria-pressed={view === 'table'} className={view === 'table' ? 'active' : ''}
                                onClick={() => changeView('table')}>Table</button>
                            <button aria-pressed={view === 'chart'} className={view === 'chart' ? 'active' : ''}
                                onClick={() => changeView('chart')}>Chart</button>
                        </div>
                        <label className="td-search">
                            <span className="td-visually-hidden">Search people or teams</span>
                            <input type="search" value={query} onChange={e => setQuery(e.target.value)}
                                placeholder="Search people or teams" />
                        </label>
                    </div>
                </header>

                {error && (
                    <div className="td-message">
                        <p>{error}</p>
                        <button className="td-btn" onClick={load}>Try again</button>
                    </div>
                )}

                {!error && !people && <p className="td-message">Loading teams…</p>}

                {people && visible.length === 0 && (
                    <p className="td-message">No people or teams match “{query}”.</p>
                )}

                {people && visible.length > 0 && view === 'chart' && (
                    <ChartView teams={visible} totalPeople={people.length} />
                )}

                {people && visible.length > 0 && view === 'table' && (
                    <div className="td-roster">
                        <div className="td-roster-head" aria-hidden="true">
                            <span>Team</span><span>Leader</span><span>Members</span>
                        </div>

                        {visible.map(t => {
                            const leaderElsewhere = t.leader && t.leader.department && t.leader.department !== t.name
                            const headcount = t.members.length + (t.leaderName ? 1 : 0)
                            return (
                                <section key={t.name} className={`td-team ${t.name === NO_TEAM ? 'is-unassigned' : ''}`}
                                    aria-label={t.name}>
                                    <div className="td-team-name">
                                        <h2>{t.name}</h2>
                                        <span>{headcount} {headcount === 1 ? 'person' : 'people'}</span>
                                    </div>

                                    <div className="td-team-leader">
                                        <span className="td-cell-label">Leader</span>
                                        {t.name === NO_TEAM ? (
                                            <p className="td-note">Set a team on these profiles in Django admin.</p>
                                        ) : t.leaderName ? (
                                            <>
                                                <div className="td-leader"><Person person={t.leader} name={t.leaderName} /></div>
                                                {leaderElsewhere && (
                                                    <p className="td-note">Their profile is in {t.leader!.department}.</p>
                                                )}
                                            </>
                                        ) : (
                                            <p className="td-note is-warn">
                                                No leader. {t.hasRecord
                                                    ? 'Pick one in Django admin under FNN - Departments.'
                                                    : 'Add this team in Django admin under FNN - Departments and pick a leader, so tickets can be routed to it.'}
                                            </p>
                                        )}
                                    </div>

                                    <div className="td-team-members">
                                        <span className="td-cell-label">Members</span>
                                        {t.members.length === 0 ? (
                                            <p className="td-note">
                                                {query ? 'No matching members.' : 'Nobody else has this team on their profile.'}
                                            </p>
                                        ) : (
                                            <ul>
                                                {t.members.map(p => (
                                                    <li key={p.id}><Person person={p} name={fullName(p)} /></li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </section>
                            )
                        })}
                    </div>
                )}
            </div>
        </div>
    )
}